import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  askLLM,
  listMeetingMessages,
  postMeetingMessage,
} from "@/api/meetings";
import type { MeetingMessage, UUID } from "@/api/types";
import { ApiError } from "@/api/errors";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * TM-d6d6bf78 + TM-2f2aa823 — chat внутри карточки встречи + LLM-Q&A.
 *
 * Sneeses ``MeetingCommentBlock`` (TM-1aaf9b30): теперь это полноценный
 * thread сообщений между оператором и LLM-ассистентом. Toggle «Спросить
 * LLM» переключает submit: off → POST /messages (просто запись),
 * on → POST /messages/ask (LLM-вызов с SSE-стримом).
 *
 * Лимит из спеки: 50 LLM-сообщений на встречу — после исчерпания
 * следующий ask возвращает 429 и UI блокирует кнопку.
 */
export interface MeetingChatBlockProps {
  meetingId: UUID;
}

const MAX_LLM_MESSAGES = 50;
const MAX_INPUT_LENGTH = 10000;

export function MeetingChatBlock({ meetingId }: MeetingChatBlockProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const messagesQuery = useQuery<MeetingMessage[]>({
    queryKey: ["meeting-messages", meetingId],
    queryFn: () => listMeetingMessages(meetingId),
  });
  const messages = useMemo(
    () => messagesQuery.data ?? [],
    [messagesQuery.data],
  );

  const [draft, setDraft] = useState("");
  const [askMode, setAskMode] = useState(false);
  const [streamingDelta, setStreamingDelta] = useState("");
  const [capError, setCapError] = useState(false);
  const threadRef = useRef<HTMLDivElement | null>(null);
  // TM-0ed9840f — skip the very first auto-scroll. On initial card load the
  // messages query resolves (0 → N) and the old `scrollIntoView` dragged the
  // WHOLE page down to the chat block. We gate the first effect run so the
  // card opens at the top like any other page.
  const didMountRef = useRef(false);

  // Counter: actually-persisted assistant messages — single source of
  // truth для cap-логики. Если меняется (например после успешного ask),
  // тост-режим автоматически обновляется.
  const assistantCount = messages.filter((m) => m.role === "assistant").length;
  const capReached = capError || assistantCount >= MAX_LLM_MESSAGES;

  useEffect(() => {
    // TM-0ed9840f — never auto-scroll the page. We only scroll the chat
    // thread's own (overflow-y-auto) container to its bottom, and only for
    // messages that appear *after* mount (sent/received while the user is
    // already in the chat), not during the initial load.
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const thread = threadRef.current;
    if (thread) {
      thread.scrollTop = thread.scrollHeight;
    }
  }, [messages.length, streamingDelta]);

  const plainMutation = useMutation({
    mutationFn: (text: string) => postMeetingMessage(meetingId, text),
    onSuccess: async () => {
      setDraft("");
      await queryClient.invalidateQueries({
        queryKey: ["meeting-messages", meetingId],
      });
    },
  });

  const askMutation = useMutation({
    mutationFn: async (text: string) => {
      setStreamingDelta("");
      return askLLM(meetingId, text, {
        onChunk: (delta) => {
          setStreamingDelta((prev) => prev + delta);
        },
      });
    },
    onSuccess: async () => {
      setDraft("");
      setStreamingDelta("");
      await queryClient.invalidateQueries({
        queryKey: ["meeting-messages", meetingId],
      });
    },
    onError: (err: unknown) => {
      setStreamingDelta("");
      if (err instanceof ApiError && err.status === 429) {
        setCapError(true);
      }
    },
  });

  const isPending = plainMutation.isPending || askMutation.isPending;

  const submitDisabled =
    draft.trim() === "" ||
    draft.length > MAX_INPUT_LENGTH ||
    isPending ||
    (askMode && capReached);

  const handleSubmit = (e?: React.FormEvent | React.KeyboardEvent) => {
    e?.preventDefault();
    if (submitDisabled) return;
    if (askMode) {
      setCapError(false);
      askMutation.mutate(draft);
    } else {
      plainMutation.mutate(draft);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      handleSubmit(e);
    }
  };

  const costPreview = useMemo(() => {
    if (!askMode) return null;
    return estimateCostUsd(messages, draft);
  }, [askMode, messages, draft]);

  return (
    <Card data-testid="meeting-chat-block">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">
          {t("meetingCard.chat.title")}
        </CardTitle>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span data-testid="meeting-chat-cap-counter">
            {t("meetingCard.chat.usage", {
              used: assistantCount,
              cap: MAX_LLM_MESSAGES,
            })}
          </span>
          <span data-testid="meeting-chat-cost-counter">
            {t("meetingCard.chat.spend", {
              cost: sumCostUsd(messages).toFixed(2),
            })}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          ref={threadRef}
          className="flex max-h-96 flex-col gap-2 overflow-y-auto rounded-md border bg-muted/30 p-3"
          data-testid="meeting-chat-thread"
        >
          {messages.length === 0 && !askMutation.isPending ? (
            <p className="text-center text-xs text-muted-foreground">
              {t("meetingCard.chat.empty")}
            </p>
          ) : null}
          {messages.map((m) => (
            <ChatBubble key={m.id} message={m} t={t} />
          ))}
          {askMutation.isPending && streamingDelta !== "" ? (
            <div
              className="self-start rounded-md bg-background px-3 py-2 text-sm shadow-sm"
              data-testid="meeting-chat-streaming-bubble"
            >
              <div className="mb-1 text-xs font-semibold text-muted-foreground">
                {t("meetingCard.chat.assistant")}
              </div>
              <div className="whitespace-pre-wrap">{streamingDelta}</div>
            </div>
          ) : null}
        </div>
        {capReached ? (
          <p
            className="text-xs text-destructive"
            role="alert"
            data-testid="meeting-chat-cap"
          >
            {t("meetingCard.chat.cap")}
          </p>
        ) : null}
        <form className="space-y-2" onSubmit={handleSubmit}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("meetingCard.chat.placeholder")}
            maxLength={MAX_INPUT_LENGTH}
            className="min-h-20 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
            data-testid="meeting-chat-input"
          />
          <div className="flex items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={askMode}
                onChange={(e) => setAskMode(e.target.checked)}
                data-testid="meeting-chat-llm-toggle"
              />
              {t("meetingCard.chat.askLLM")}
            </label>
            <div className="flex items-center gap-2">
              {costPreview !== null ? (
                <span
                  className="text-xs text-muted-foreground"
                  data-testid="meeting-chat-cost-preview"
                >
                  {t("meetingCard.chat.costPreview", {
                    cost: costPreview.toFixed(3),
                  })}
                </span>
              ) : null}
              <Button
                type="submit"
                size="sm"
                disabled={submitDisabled}
                data-testid="meeting-chat-send"
              >
                {isPending ? t("common.saving") : t("meetingCard.chat.send")}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function ChatBubble({
  message,
  t,
}: {
  message: MeetingMessage;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const isUser = message.role === "user";
  const isSystem = message.role === "system";
  if (isSystem) {
    return (
      <div
        className="self-center text-center text-xs text-muted-foreground"
        data-testid="meeting-chat-bubble-system"
      >
        {message.text}
      </div>
    );
  }
  // Show the actual author name when the BE could resolve it — there can
  // be more than one operator on a meeting card, so a generic «Вы» on
  // every bubble would erase who said what. Fallback to «Вы» only when
  // the row truly has no name (orphan author_id from a soft-deleted user).
  const label = isUser
    ? (message.author_name ?? t("meetingCard.chat.you"))
    : t("meetingCard.chat.assistant");
  return (
    <div
      className={`max-w-[80%] rounded-md px-3 py-2 text-sm shadow-sm ${
        isUser
          ? "self-end bg-primary text-primary-foreground"
          : "self-start bg-background"
      }`}
      data-testid={
        isUser ? "meeting-chat-bubble-user" : "meeting-chat-bubble-assistant"
      }
    >
      <div className="mb-1 text-xs font-semibold opacity-70">{label}</div>
      <div className="whitespace-pre-wrap">{message.text}</div>
    </div>
  );
}

// ---- Cost helpers ---------------------------------------------------------

function sumCostUsd(messages: MeetingMessage[]): number {
  let total = 0;
  for (const m of messages) {
    const raw = m.llm_meta?.cost_usd;
    if (typeof raw === "string") {
      const n = Number(raw);
      if (Number.isFinite(n)) total += n;
    } else if (typeof raw === "number") {
      total += raw;
    }
  }
  return total;
}

/**
 * Cost-preview оценка для UI: ~$15 / 1M input + ~$75 / 1M output
 * (Opus 4 base rates) на длину «вопрос + 5% контекста». Сильно
 * заниженная оценка — нам важно дать пользователю порядок цифры,
 * не точный счёт. Реальная стоимость считается на BE и приходит
 * в `llm_meta.cost_usd`.
 */
function estimateCostUsd(messages: MeetingMessage[], draft: string): number {
  const inputTokens =
    approxTokens(draft) +
    Math.round(approxTokens(messages.map((m) => m.text).join(" ")) * 0.05);
  const outputTokens = 300;
  return (inputTokens * 15) / 1_000_000 + (outputTokens * 75) / 1_000_000;
}

function approxTokens(text: string): number {
  // 1 токен ≈ 4 символа для русского — грубая оценка достаточна для
  // preview. Точное токенизирование требует tokeniser-bundle, которого
  // в bundle нет.
  return Math.ceil(text.length / 4);
}
